import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Circle,
  MapContainer,
  Marker,
  Polyline,
  Popup,
  TileLayer,
  Tooltip,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import api from "../api/axios";
import { API_ORIGIN } from "../api/apiConfig";
import AdminShell from "../components/AdminShell";
import GpsFleetDashboard from "../components/GpsFleetDashboard";
import GpsRouteReplay from "../components/GpsRouteReplay";
import GpsTripsActivity from "../components/GpsTripsActivity";
import GpsDeviceAssignmentPanel from "../components/GpsDeviceAssignmentPanel";
import { GPS_SIMULATION_CONTROLS_ENABLED } from "../config/gpsRuntimeConfig";
import "leaflet/dist/leaflet.css";
import "../styles/admin.css";

const GPS_STATUS_OPTIONS = ["active", "delayed", "offline", "returned"];
const RADIUS_OPTIONS = [5000, 10000, 15000];
const REFRESH_SECONDS = 30;

const LOCATION_PRESETS = {
  "": { label: "Select preset...", lat: "", lng: "" },
  naga: { label: "Bicol / Naga City", lat: 13.6218, lng: 123.1948 },
  cebu: { label: "Cebu City", lat: 10.3157, lng: 123.8854 },
  davao: { label: "Davao City", lat: 7.1907, lng: 125.4553 },
  manila: { label: "Manila", lat: 14.5995, lng: 120.9842 },
  baguio: { label: "Baguio City", lat: 16.4023, lng: 120.596 },
  tagaytay: { label: "Tagaytay", lat: 14.1153, lng: 120.962 },
  cavite: { label: "Dasmarinas / Cavite", lat: 14.3294, lng: 120.9367 },
  custom: { label: "Custom Coordinates", lat: "", lng: "" },
};

function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M10.8 4a6.8 6.8 0 0 1 5.4 10.9l3.5 3.5-1.4 1.4-3.5-3.5A6.8 6.8 0 1 1 10.8 4Zm0 2a4.8 4.8 0 1 0 0 9.6 4.8 4.8 0 0 0 0-9.6Z" />
    </svg>
  );
}

function PinIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2a7 7 0 0 0-7 7c0 5.2 7 13 7 13s7-7.8 7-13a7 7 0 0 0-7-7Zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5Z" />
    </svg>
  );
}

function StatusIcon({ type }) {
  const paths = {
    active: "M12 2 4 5v6c0 5 3.4 9.4 8 10.7 4.6-1.3 8-5.7 8-10.7V5l-8-3Zm-1 13.3-3-3 1.4-1.4 1.6 1.6 4-4 1.4 1.4-5.4 5.4Z",
    delayed: "M12 3a9 9 0 1 1 0 18 9 9 0 0 1 0-18Zm1 4h-2v6l5 3 1-1.7-4-2.3V7Z",
    offline: "M3.3 2.4 21.6 20.7l-1.3 1.3-3.1-3.1A10.8 10.8 0 0 1 12 20a11 11 0 0 1-9.8-6l2.4-2.4L2 9l1.3-1.3 18.3 18.3-1.3 1.3-17-17-1.3 1.3 2.7 2.7A8.9 8.9 0 0 0 12 18c1.2 0 2.3-.2 3.3-.6L3.3 5.4V2.4Zm16 4.6 2.5 2.5-2.3 2.3-2.5-2.5L19.3 7Z",
    returned: "M12 3a9 9 0 1 1 0 18 9 9 0 0 1 0-18Zm4.2 6.7-1.4-1.4-4 4-1.6-1.6-1.4 1.4 3 3 5.4-5.4Z",
    zone: "M12 2a7 7 0 0 0-7 7c0 5.2 7 13 7 13s7-7.8 7-13a7 7 0 0 0-7-7Zm0 9.7A2.7 2.7 0 1 1 12 6.3a2.7 2.7 0 0 1 0 5.4Z",
    alert: "M12 2 2 20h20L12 2Zm1 14h-2v2h2v-2Zm0-7h-2v5h2V9Z",
    back: "M20 11H7.8l5.6-5.6L12 4 4 12l8 8 1.4-1.4L7.8 13H20v-2Z",
    refresh: "M17.7 6.3A8 8 0 0 0 4.3 10H2l3 3.2L8 10H6.4a6 6 0 0 1 9.9-2.3L14 10h6V4l-2.3 2.3ZM6.3 17.7A8 8 0 0 0 19.7 14H22l-3-3.2-3 3.2h1.6a6 6 0 0 1-9.9 2.3L10 14H4v6l2.3-2.3Z",
    map: "M15 5 9 3 3 5v16l6-2 6 2 6-2V3l-6 2Zm-1 13.7-4-1.3V5.3l4 1.3v12.1Z",
  };

  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d={paths[type] || paths.zone} />
    </svg>
  );
}

function formatDate(value) {
  const parsed = value ? new Date(value) : null;
  if (!parsed || Number.isNaN(parsed.getTime())) return "N/A";
  return parsed.toISOString().slice(0, 10);
}

function formatDateTime(value) {
  const parsed = value ? new Date(value) : null;
  if (!parsed || Number.isNaN(parsed.getTime())) return "N/A";
  return parsed.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatFullDateTime(value) {
  const parsed = value ? new Date(value) : null;
  if (!parsed || Number.isNaN(parsed.getTime())) return "N/A";
  return parsed.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function titleCase(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function formatKm(meters) {
  const value = Number(meters || 0);
  if (value < 1000) return `${Math.round(value)} m`;
  if (value % 1000 === 0) return `${value / 1000} km`;
  return `${(value / 1000).toFixed(value >= 10000 ? 1 : 2)} km`;
}

function formatPeso(value) {
  return `PHP ${Math.round(Number(value || 0)).toLocaleString()}`;
}

function normalizeCoord(coord) {
  const lat = Number(coord?.lat ?? coord?.latitude);
  const lng = Number(coord?.lng ?? coord?.lon ?? coord?.long ?? coord?.longitude);
  if (
    !Number.isFinite(lat) ||
    !Number.isFinite(lng) ||
    lat < -90 ||
    lat > 90 ||
    lng < -180 ||
    lng > 180
  ) {
    return null;
  }
  return { lat, lng };
}

function coordToLatLng(coord) {
  const normalized = normalizeCoord(coord);
  return normalized ? [normalized.lat, normalized.lng] : null;
}

function formatCoord(coord) {
  const normalized = normalizeCoord(coord);
  if (!normalized) return "-";
  return `${normalized.lat.toFixed(5)}, ${normalized.lng.toFixed(5)}`;
}

function getDistanceMeters(a, b) {
  const start = normalizeCoord(a);
  const end = normalizeCoord(b);
  if (!start || !end) return Number.POSITIVE_INFINITY;

  const toRad = (value) => (value * Math.PI) / 180;
  const dLat = toRad(end.lat - start.lat);
  const dLng = toRad(end.lng - start.lng);
  const lat1 = toRad(start.lat);
  const lat2 = toRad(end.lat);
  const haversine =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;

  return 2 * 6371000 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
}

function getNearestRoutePoint(currentCoords, routePoints = []) {
  const current = normalizeCoord(currentCoords);
  if (!current || !routePoints.length) return null;

  return routePoints.reduce(
    (nearest, point) => {
      const distance = getDistanceMeters(current, point);
      return distance < nearest.distance ? { point, distance } : nearest;
    },
    { point: null, distance: Number.POSITIVE_INFINITY }
  ).point;
}

function createMapIcon(type, isAlert = false) {
  const className = [
    "gps-leaflet-marker",
    `gps-leaflet-marker--${type}`,
    isAlert ? "is-alert" : "",
  ].filter(Boolean).join(" ");

  const labels = {
    pickup: "P",
    destination: "D",
    vehicle: "V",
  };

  return L.divIcon({
    className,
    html: `<span>${labels[type] || ""}</span>`,
    iconSize: [34, 34],
    iconAnchor: [17, 17],
    popupAnchor: [0, -18],
  });
}

function MapViewport({ points }) {
  const map = useMap();

  useEffect(() => {
    const validPoints = points
      .map(coordToLatLng)
      .filter(Boolean);

    if (validPoints.length > 1) {
      map.fitBounds(validPoints, {
        padding: [54, 54],
        maxZoom: 14,
      });
      return;
    }

    if (validPoints.length === 1) {
      map.setView(validPoints[0], 13);
    }
  }, [map, points]);

  return null;
}

function TrackingMap({
  booking,
  tracking,
  zoneStatus,
  allowedRadius,
  currentDistance,
  destination,
  fallbackNote,
  routeFallbackNote,
}) {
  const pickupCoords = normalizeCoord(tracking?.pickupCoords || booking?.pickupCoords);
  const dropoffCoords = normalizeCoord(
    tracking?.dropoffCoords ||
      booking?.dropoffCoords ||
      booking?.destinationCoords
  );
  const currentCoords = normalizeCoord(tracking?.currentCoords);
  const routePoints = Array.isArray(tracking?.route)
    ? tracking.route.map(normalizeCoord).filter(Boolean)
    : [];
  const routeLine =
    routePoints.length >= 2
      ? routePoints
      : pickupCoords && dropoffCoords
      ? [pickupCoords, dropoffCoords]
      : [];
  const isOutOfRoute = zoneStatus === "out_of_zone";
  const circleCenter =
    getNearestRoutePoint(currentCoords, routeLine) ||
    currentCoords ||
    dropoffCoords ||
    pickupCoords;
  const mapPoints = [
    pickupCoords,
    dropoffCoords,
    currentCoords,
    circleCenter,
    ...routeLine,
  ].filter(Boolean);
  const hasUsableMap = mapPoints.length > 0 && (pickupCoords || dropoffCoords || fallbackNote || routeFallbackNote);
  const zoneLabel = getZoneLabel(zoneStatus);
  const currentLabel = getCurrentVehicleLabel(tracking?.currentLocationLabel);
  const initialCenter = coordToLatLng(currentCoords || pickupCoords || dropoffCoords || mapPoints[0]);

  if (!hasUsableMap) {
    return (
      <div className="gps-map-empty-state">
        <span className="gps-map-empty-state__icon">
          <StatusIcon type="map" />
        </span>
        <div>
          <strong>Map unavailable</strong>
          <p>Pickup and destination coordinates are missing for this booking.</p>
          <small>Update the booking location details to enable route monitoring.</small>
        </div>
      </div>
    );
  }

  return (
    <div className="gps-leaflet-wrap">
      <MapContainer
        className="gps-leaflet-map"
        center={initialCenter}
        zoom={12}
        scrollWheelZoom
      >
        <MapViewport points={mapPoints} />
        <TileLayer
          attribution="&copy; OpenStreetMap contributors"
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {routeLine.length >= 2 ? (
          <Polyline
            positions={routeLine.map((point) => [point.lat, point.lng])}
            pathOptions={{
              color: "#2563eb",
              weight: 5,
              opacity: 0.88,
            }}
          />
        ) : null}

        {circleCenter ? (
          <Circle
            center={[circleCenter.lat, circleCenter.lng]}
            radius={Number(allowedRadius || 10000)}
            pathOptions={{
              color: isOutOfRoute ? "#dc2626" : "#16a34a",
              fillColor: isOutOfRoute ? "#ef4444" : "#22c55e",
              fillOpacity: 0.12,
              opacity: 0.72,
              weight: 2,
            }}
          />
        ) : null}

        {pickupCoords ? (
          <Marker position={[pickupCoords.lat, pickupCoords.lng]} icon={createMapIcon("pickup")}>
            <Tooltip direction="top" offset={[0, -18]}>Pickup</Tooltip>
            <Popup>
              <strong>Pickup</strong>
              <p>{booking?.pickupLocation || "Pickup location"}</p>
            </Popup>
          </Marker>
        ) : null}

        {dropoffCoords ? (
          <Marker position={[dropoffCoords.lat, dropoffCoords.lng]} icon={createMapIcon("destination")}>
            <Tooltip direction="top" offset={[0, -18]}>Destination</Tooltip>
            <Popup>
              <strong>Destination</strong>
              <p>{destination || booking?.destination || "Destination"}</p>
            </Popup>
          </Marker>
        ) : null}

        {currentCoords ? (
          <Marker
            position={[currentCoords.lat, currentCoords.lng]}
            icon={createMapIcon("vehicle", isOutOfRoute)}
          >
            <Tooltip direction="top" offset={[0, -18]}>Current Vehicle</Tooltip>
            <Popup>
              <strong>Live vehicle location</strong>
              <p>{currentLabel}</p>
              <p>Distance from Expected Route: {formatKm(currentDistance)}</p>
              <p>Status: {zoneLabel}</p>
            </Popup>
          </Marker>
        ) : null}
      </MapContainer>

      {routeFallbackNote ? (
        <div className="gps-map-fallback-note">{routeFallbackNote}</div>
      ) : fallbackNote ? (
        <div className="gps-map-fallback-note">Using fallback route coordinates.</div>
      ) : null}
    </div>
  );
}

function getVehicleImage(vehicle) {
  const raw =
    vehicle?.image ||
    vehicle?.imageUrl ||
    vehicle?.mainImage ||
    vehicle?.photo ||
    (Array.isArray(vehicle?.images) ? vehicle.images[0] : "");

  if (!raw) return "";
  if (String(raw).startsWith("http") || String(raw).startsWith("data:")) return raw;
  return `${API_ORIGIN}${raw}`;
}

function normalizeBookingStatus(status) {
  const normalized = String(status || "").toLowerCase();
  if (["approved", "confirmed", "booked_confirmed", "awaiting_payment"].includes(normalized)) return "approved";
  if (normalized === "completed") return "completed";
  return "pending";
}

function getGpsStatusFromTracking(tracking = {}, bookingStatus = "") {
  const status = String(tracking.status || "").toLowerCase();
  if (status === "completed" || String(bookingStatus).toLowerCase() === "completed") return "returned";
  if (tracking.isActive) {
    const updatedAt = tracking.lastUpdatedAt ? new Date(tracking.lastUpdatedAt).getTime() : 0;
    if (updatedAt && Date.now() - updatedAt > 30 * 60 * 1000) return "delayed";
    return "active";
  }
  return status === "completed" ? "returned" : "offline";
}

function getGpsStatus(booking) {
  return getGpsStatusFromTracking(booking?.tracking || {}, booking?.status);
}

function getZoneStatusFromTracking(tracking = {}) {
  const status = String(tracking.status || "").toLowerCase();
  const mode = String(tracking.mode || "").toLowerCase();
  if (status === "out_of_the_way" || mode === "out_of_the_way") return "out_of_zone";
  if (status === "on_route" || status === "completed") return "normal";
  if (mode === "normal" && tracking.currentCoords && Array.isArray(tracking.route) && tracking.route.length > 0) {
    return "normal";
  }
  return "unknown";
}

function getZoneLabel(zoneStatus) {
  if (zoneStatus === "out_of_zone") return "Out of Route";
  if (zoneStatus === "normal") return "On Route";
  return "No active trip";
}

function getTrackingStatusLabel(status) {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "active") return "Active";
  if (normalized === "delayed") return "Delayed";
  if (normalized === "returned") return "Completed";
  if (normalized === "offline") return "No active trip";
  return titleCase(status);
}

function getCurrentVehicleLabel(label) {
  const cleanLabel = String(label || "").trim();
  if (!cleanLabel || cleanLabel.toLowerCase() === "current route point") {
    return "Current vehicle location";
  }
  return cleanLabel;
}

function getZoneStatus(booking) {
  return getZoneStatusFromTracking(booking?.tracking || {});
}

function buildTrackingRow(booking) {
  const vehicle = booking?.vehicleId || {};
  const tracking = booking?.tracking || {};
  const gpsStatus = getGpsStatus(booking);
  const zoneStatus = getZoneStatus(booking);
  const radius = Number(tracking.allowedRadiusMeters || 10000);
  const bookingStatus = normalizeBookingStatus(booking?.status);

  return {
    id: booking?._id,
    vehicleName: [vehicle?.make, vehicle?.model].filter(Boolean).join(" ") || "Unassigned Vehicle",
    plateNo: vehicle?.plateNo || "No plate",
    category: vehicle?.category || "Vehicle",
    image: getVehicleImage(vehicle),
    customerName: booking?.fullName || booking?.userId?.name || "Unknown Customer",
    contact: booking?.contact || booking?.userId?.contact || booking?.email || "No contact",
    destination: booking?.destination || "Unknown destination",
    radiusKm: Math.round(radius / 1000),
    startDate: booking?.startDate,
    endDate: booking?.endDate,
    bookingStatus,
    gpsStatus,
    zoneStatus,
    lastUpdate: tracking.lastUpdatedAt || booking?.updatedAt || booking?.createdAt,
    searchable: [
      vehicle?.make,
      vehicle?.model,
      vehicle?.plateNo,
      vehicle?.category,
      booking?.fullName,
      booking?.contact,
      booking?.email,
      booking?.destination,
      booking?.bookingReference,
    ].filter(Boolean).join(" ").toLowerCase(),
  };
}

function mergeTrackingIntoBooking(booking, tracking) {
  if (!booking) return null;
  return { ...booking, tracking: tracking || booking.tracking || {} };
}

function getDetailModel(booking, trackingOverride) {
  const merged = mergeTrackingIntoBooking(booking, trackingOverride);
  if (!merged) return null;
  return buildTrackingRow(merged);
}

function DetailInfoRow({ label, value }) {
  return (
    <div className="gps-detail-info-row">
      <span>{label}</span>
      <strong>{value || "-"}</strong>
    </div>
  );
}

function LegacyBookingTracking() {
  const navigate = useNavigate();
  const { bookingId } = useParams();
  const [bookings, setBookings] = useState([]);
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [error, setError] = useState("");
  const [actionMsg, setActionMsg] = useState("");
  const [actionTone, setActionTone] = useState("success");
  const [actionKey, setActionKey] = useState("");
  const [search, setSearch] = useState("");
  const [gpsFilter, setGpsFilter] = useState("all");
  const [zoneFilter, setZoneFilter] = useState("all");
  const [bookingFilter, setBookingFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [customDate, setCustomDate] = useState("");
  const [refreshCountdown, setRefreshCountdown] = useState(REFRESH_SECONDS);
  const [scenario, setScenario] = useState("normal");
  const [preset, setPreset] = useState("");
  const [customLat, setCustomLat] = useState("");
  const [customLng, setCustomLng] = useState("");
  const [customLabel, setCustomLabel] = useState("");
  const [scenarioValidation, setScenarioValidation] = useState("");

  const loadBookings = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const res = await api.get("/bookings", { params: { limit: 100 } });
      setBookings(Array.isArray(res.data?.bookings) ? res.data.bookings : []);
      setError("");
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load live tracking data.");
      setBookings([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  const loadTracking = useCallback(async ({ silent = false } = {}) => {
    if (!bookingId) return;
    try {
      if (!silent) setTrackingLoading(true);
      const res = await api.get(`/bookings/${bookingId}/tracking`);
      setTracking(res.data?.tracking || null);
      setActionMsg("");
      setActionTone("success");
      setActionKey("");
      setRefreshCountdown(REFRESH_SECONDS);
    } catch (err) {
      setActionMsg(err?.response?.data?.message || "Failed to refresh tracking state.");
      setActionTone("danger");
    } finally {
      if (!silent) setTrackingLoading(false);
    }
  }, [bookingId]);

  useEffect(() => {
    loadBookings();
  }, [loadBookings]);

  useEffect(() => {
    loadTracking();
  }, [loadTracking]);

  useEffect(() => {
    if (!bookingId) return undefined;
    const countdown = window.setInterval(() => {
      setRefreshCountdown((current) => (current <= 1 ? REFRESH_SECONDS : current - 1));
    }, 1000);
    return () => window.clearInterval(countdown);
  }, [bookingId]);

  useEffect(() => {
    if (!bookingId) {
      const timer = window.setInterval(() => loadBookings({ silent: true }), 30000);
      return () => window.clearInterval(timer);
    }

    const timer = window.setInterval(() => {
      loadTracking({ silent: true });
      loadBookings({ silent: true });
    }, REFRESH_SECONDS * 1000);
    return () => window.clearInterval(timer);
  }, [bookingId, loadBookings, loadTracking]);

  useEffect(() => {
    const selected = LOCATION_PRESETS[preset];
    if (!selected) return;

    if (preset === "custom") {
      setCustomLat("");
      setCustomLng("");
      setCustomLabel("");
      setScenarioValidation("");
      return;
    }

    if (!preset) {
      setScenarioValidation("");
      return;
    }

    setCustomLat(selected.lat);
    setCustomLng(selected.lng);
    setCustomLabel(selected.label);
    setScenarioValidation("");
  }, [preset]);

  const rows = useMemo(() => {
    return bookings
      .filter((booking) => !["cancelled", "rejected", "expired", "draft"].includes(String(booking?.status || "").toLowerCase()))
      .map(buildTrackingRow)
      .filter((row) => row.id);
  }, [bookings]);

  const currentBooking = useMemo(() => {
    return bookings.find((booking) => booking?._id === bookingId) || null;
  }, [bookings, bookingId]);

  const detailBooking = useMemo(() => mergeTrackingIntoBooking(currentBooking, tracking), [currentBooking, tracking]);
  const detail = useMemo(() => getDetailModel(currentBooking, tracking), [currentBooking, tracking]);
  const detailTracking = useMemo(
    () => detailBooking?.tracking || tracking || {},
    [detailBooking, tracking]
  );
  const detailVehicle = detailBooking?.vehicleId || {};
  const detailDriver = detailBooking?.driverId || {};
  const zoneStatus = getZoneStatusFromTracking(detailTracking);
  const gpsStatus = getGpsStatusFromTracking(detailTracking, detailBooking?.status);
  const isOutOfZone = zoneStatus === "out_of_zone";
  const fallbackSimulationNote = detailTracking.usedFallbackCoords
    ? detailTracking.fallbackReason ||
      "Using fallback route coordinates because exact booking coordinates are not available."
    : "";
  const routeSource = String(detailTracking.routeSource || "").toLowerCase();
  const isFallbackRoute = routeSource === "fallback";
  const routeSourceLabel =
    routeSource === "osrm"
      ? "Road Routing"
      : isFallbackRoute
      ? "Fallback Route"
      : "Route Monitoring";
  const routeLegendLabel = routeSource === "osrm" ? "Expected Road Route" : "Expected Route";
  const routeFallbackNote =
    isFallbackRoute
      ? detailTracking.routeFallbackReason ||
        "Using fallback expected route because road routing is unavailable."
      : "";
  const isCustomScenario = scenario === "custom_out_of_route";
  const allowedRadius = Number(detailTracking.allowedRadiusMeters || 10000);
  const currentDistance = Number(detailTracking.currentDistanceFromRouteMeters || 0);
  const distancePercent = allowedRadius ? Math.min((currentDistance / allowedRadius) * 100, 100) : 0;
  const customLatNumber = Number(customLat);
  const customLngNumber = Number(customLng);
  const hasValidCustomCoords =
    Number.isFinite(customLatNumber) &&
    Number.isFinite(customLngNumber) &&
    customLatNumber >= -90 &&
    customLatNumber <= 90 &&
    customLngNumber >= -180 &&
    customLngNumber <= 180;
  const customScenarioMessage =
    isCustomScenario && !hasValidCustomCoords
      ? "Select a preset location or enter valid latitude and longitude."
      : scenarioValidation;
  const scenarioButtonLabel =
    scenario === "normal"
      ? "Apply Normal Route"
      : scenario === "slight_detour"
      ? "Apply Slight Detour"
      : "Apply Out-of-Route Location";

  const counts = useMemo(() => ({
    active: rows.filter((row) => row.gpsStatus === "active").length,
    delayed: rows.filter((row) => row.gpsStatus === "delayed").length,
    offline: rows.filter((row) => row.gpsStatus === "offline").length,
    returned: rows.filter((row) => row.gpsStatus === "returned").length,
    outOfZone: rows.filter((row) => row.zoneStatus === "out_of_zone").length,
  }), [rows]);

  const selectedCard = useMemo(() => {
    if (zoneFilter === "out_of_zone" && gpsFilter === "all") return "out_of_zone";
    if (GPS_STATUS_OPTIONS.includes(gpsFilter) && zoneFilter === "all") return gpsFilter;
    return "";
  }, [gpsFilter, zoneFilter]);

  const resolvedDateFilter = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (dateFilter === "today") return today.toISOString().slice(0, 10);
    if (dateFilter === "tomorrow") {
      const tomorrow = new Date(today);
      tomorrow.setDate(today.getDate() + 1);
      return tomorrow.toISOString().slice(0, 10);
    }
    if (dateFilter === "this_week") {
      const end = new Date(today);
      end.setDate(today.getDate() + 6);
      return { from: today.toISOString().slice(0, 10), to: end.toISOString().slice(0, 10) };
    }
    if (dateFilter === "custom" && customDate) return customDate;
    return null;
  }, [dateFilter, customDate]);

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (query && !row.searchable.includes(query)) return false;
      if (gpsFilter !== "all" && row.gpsStatus !== gpsFilter) return false;
      if (zoneFilter !== "all" && row.zoneStatus !== zoneFilter) return false;
      if (bookingFilter !== "all" && row.bookingStatus !== bookingFilter) return false;
      if (resolvedDateFilter) {
        const rowStart = row.startDate ? new Date(row.startDate) : null;
        const rowEnd = row.endDate ? new Date(row.endDate) : null;
        if (!rowStart || !rowEnd) return false;
        rowStart.setHours(0, 0, 0, 0);
        rowEnd.setHours(23, 59, 59, 999);
        if (typeof resolvedDateFilter === "string") {
          const target = new Date(`${resolvedDateFilter}T00:00:00`);
          if (target < rowStart || target > rowEnd) return false;
        } else {
          const filterFrom = new Date(`${resolvedDateFilter.from}T00:00:00`);
          const filterTo = new Date(`${resolvedDateFilter.to}T23:59:59`);
          if (rowEnd < filterFrom || rowStart > filterTo) return false;
        }
      }
      return true;
    });
  }, [rows, search, gpsFilter, zoneFilter, bookingFilter, resolvedDateFilter]);

  const outOfZoneRow = useMemo(() => rows.find((row) => row.zoneStatus === "out_of_zone") || null, [rows]);

  const routeHistory = useMemo(() => {
    const route = Array.isArray(detailTracking.route) ? detailTracking.route : [];
    const routeIndex = Math.max(
      0,
      Math.min(Number(detailTracking.routeIndex || 0), Math.max(route.length - 1, 0))
    );
    const startIndex = Math.max(0, routeIndex - 4);
    const points = route.slice(startIndex, routeIndex + 1).map((point, offset) => {
      const index = startIndex + offset;
      return {
        label:
          index === 0
            ? detailBooking?.pickupLocation || "Pickup location"
            : `Route checkpoint ${index + 1}`,
        time: index === routeIndex ? detailTracking.lastUpdatedAt : detailTracking.startedAt,
        meta: `${formatCoord(point)} - route checkpoint`,
      };
    });

    if (detailTracking.currentCoords) {
      const currentCoords = normalizeCoord(detailTracking.currentCoords);
      const currentRoutePoint = normalizeCoord(route[routeIndex]);
      const currentAlreadyShown =
        currentCoords &&
        currentRoutePoint &&
        Math.abs(currentCoords.lat - currentRoutePoint.lat) < 0.000001 &&
        Math.abs(currentCoords.lng - currentRoutePoint.lng) < 0.000001;

      if (currentAlreadyShown && points.length > 0) {
        points[points.length - 1] = {
          label: getCurrentVehicleLabel(detailTracking.currentLocationLabel),
          time: detailTracking.lastUpdatedAt,
          meta: `${formatCoord(currentCoords)} - ${formatKm(currentDistance)} from expected route`,
        };
        return points;
      }

      points.push({
        label: getCurrentVehicleLabel(detailTracking.currentLocationLabel),
        time: detailTracking.lastUpdatedAt,
        meta: `${formatCoord(currentCoords)} - ${formatKm(currentDistance)} from expected route`,
      });
    }

    return points;
  }, [detailBooking, detailTracking, currentDistance]);

  const handleSummaryClick = (type) => {
    if (type === "out_of_zone") {
      const nextSelected = selectedCard === type;
      setZoneFilter(nextSelected ? "all" : "out_of_zone");
      setGpsFilter("all");
      return;
    }

    const nextSelected = selectedCard === type;
    setGpsFilter(nextSelected ? "all" : type);
    setZoneFilter("all");
  };

  const handleTrack = (row) => {
    navigate(`/gps-tracking/${row.id}`);
  };

  const clearListFilters = () => {
    setSearch("");
    setGpsFilter("all");
    setZoneFilter("all");
    setBookingFilter("all");
    setDateFilter("all");
    setCustomDate("");
  };

  const runTrackingAction = async (path, options = {}) => {
    if (!bookingId) return;
    try {
      setActionKey(options.actionKey || path);
      setTrackingLoading(true);
      const method = options.method || "post";
      const body = options.body;
      const res = method === "patch"
        ? await api.patch(path, body)
        : await api.post(path, body);
      setTracking(res.data?.tracking || null);
      setActionMsg(options.success || "Tracking updated.");
      setActionTone("success");
      setRefreshCountdown(REFRESH_SECONDS);
      loadBookings({ silent: true });
    } catch (err) {
      setActionMsg(err?.response?.data?.message || options.error || "Tracking action failed.");
      setActionTone("danger");
    } finally {
      setTrackingLoading(false);
      setActionKey("");
    }
  };

  const applyScenario = () => {
    if (scenario === "custom_out_of_route") {
      if (!hasValidCustomCoords) {
        setScenarioValidation("Latitude and longitude are required and must be valid numbers.");
        return;
      }

      runTrackingAction(`/bookings/${bookingId}/tracking/scenario`, {
        body: {
          scenario,
          customCoords: { lat: customLatNumber, lng: customLngNumber },
          customLocationLabel: customLabel.trim() || "Custom out-of-route location",
        },
        success: "Custom out-of-route scenario applied.",
        actionKey: "scenario",
      });
      return;
    }

    runTrackingAction(`/bookings/${bookingId}/tracking/scenario`, {
      body: { scenario },
      success: "Route monitoring scenario applied.",
      actionKey: "scenario",
    });
  };

  const handleScenarioChange = (value) => {
    setScenario(value);
    setScenarioValidation("");
    if (value !== "custom_out_of_route") {
      setPreset("");
      setCustomLat("");
      setCustomLng("");
      setCustomLabel("");
    }
  };

  const headerActions = !bookingId ? (
    <div className="gps-header-actions">
      <button
        type="button"
        className="gps-top-alert-pill"
        onClick={() => outOfZoneRow && handleTrack(outOfZoneRow)}
        disabled={!outOfZoneRow}
      >
        <StatusIcon type="alert" />
        <strong>{counts.outOfZone}</strong>
        Out of Route Alert
      </button>
      <span className="gps-live-indicator">
        <span />
        Fleet Monitoring
      </span>
    </div>
  ) : null;

  if (bookingId) {
    const coords = detailTracking.currentCoords || {};
    const hasCoords = Number.isFinite(Number(coords.lat)) && Number.isFinite(Number(coords.lng));
    const mapsUrl = hasCoords ? `https://www.google.com/maps?q=${coords.lat},${coords.lng}` : "";
    const mapZoneLabel = getZoneLabel(zoneStatus);

    return (
      <AdminShell
        pageClassName="admin-shell--live-monitoring"
        eyebrow="FLEET MONITORING"
        title="Live Monitoring"
        subtitle="Monitor active rentals, route status, and location updates from one dashboard."
      >
        <div className="gps-detail-page">
          {isOutOfZone && detail ? (
            <div className="gps-detail-alert-strip">
              <span><StatusIcon type="alert" /></span>
              <div>
                <strong>Out of Route Alert - {detail.vehicleName} ({detail.plateNo})</strong>
                <small>
                  {detail.customerName} is {formatKm(currentDistance)} from the expected route ({formatKm(allowedRadius)} allowed route radius).
                </small>
              </div>
              <em>{formatDateTime(detailTracking.lastUpdatedAt)}</em>
            </div>
          ) : null}

          <header className="gps-detail-topbar">
            <button type="button" className="gps-detail-back" onClick={() => navigate("/gps-tracking")}>
              <StatusIcon type="back" />
              Back to Live Monitoring
            </button>

            {detail ? (
              <div className="gps-detail-vehicle-head">
                <div className="gps-vehicle-thumb">
                  {detail.image ? <img src={detail.image} alt={detail.vehicleName} /> : <span>{detail.vehicleName.charAt(0)}</span>}
                </div>
                <div>
                  <strong>{detail.vehicleName}</strong>
                  <small>{detail.plateNo} - {detail.category}</small>
                </div>
                <span className={`gps-badge gps-badge--zone-${zoneStatus}`}>
                  <StatusIcon type={isOutOfZone ? "alert" : zoneStatus === "normal" ? "active" : "returned"} />
                  {getZoneLabel(zoneStatus)}
                </span>
              </div>
            ) : null}

            <div className="gps-detail-top-actions">
              <div className="gps-radius-control">
                <span><StatusIcon type="refresh" /> Allowed Route Radius:</span>
                {RADIUS_OPTIONS.map((radius) => (
                  <button
                    type="button"
                    key={radius}
                    className={allowedRadius === radius ? "active" : ""}
                    onClick={() => runTrackingAction(`/bookings/${bookingId}/tracking/radius`, {
                      method: "patch",
                      body: { allowedRadiusMeters: radius },
                      success: "Allowed route radius updated.",
                      actionKey: `radius-${radius}`,
                    })}
                    disabled={trackingLoading}
                  >
                    {actionKey === `radius-${radius}` ? "Updating..." : `${radius / 1000} km`}
                  </button>
                ))}
              </div>
              <span className="gps-detail-refresh-text">Auto-refresh in <strong>{refreshCountdown}s</strong></span>
              <button type="button" className="gps-detail-refresh-btn" onClick={() => loadTracking()} disabled={trackingLoading}>
                <StatusIcon type="refresh" />
                Refresh
              </button>
            </div>
          </header>

          {loading && !detail ? <div className="gps-empty-cell">Loading tracking detail...</div> : null}
          {error && !detail ? <div className="gps-empty-cell gps-empty-cell--error">{error}</div> : null}

          {detail ? (
            <>
              <div className="gps-detail-summary-grid">
                <section className={`gps-detail-summary-card gps-detail-summary-card--${gpsStatus}`}>
                  <span className="gps-detail-summary-card__icon"><StatusIcon type="zone" /></span>
                  <small>Tracking Source</small>
                  <span className={`gps-status gps-status--${gpsStatus}`}>
                    <i />
                    <span>Route monitoring</span>
                  </span>
                  <p>{getTrackingStatusLabel(gpsStatus)}</p>
                  <div className="gps-signal-bars"><i /><i /><i /><i /></div>
                </section>
                <section className={`gps-detail-summary-card gps-detail-summary-card--zone-${zoneStatus}`}>
                  <span className="gps-detail-summary-card__icon"><StatusIcon type={isOutOfZone ? "alert" : "active"} /></span>
                  <small>Out of Route Alert</small>
                  <span className={`gps-badge gps-badge--zone-${zoneStatus}`}>
                    <StatusIcon type={isOutOfZone ? "alert" : zoneStatus === "normal" ? "active" : "returned"} />
                    {getZoneLabel(zoneStatus)}
                  </span>
                  <p>{formatKm(currentDistance)} from expected route</p>
                </section>
                <section className="gps-detail-summary-card">
                  <span className="gps-detail-summary-card__icon"><StatusIcon type="returned" /></span>
                  <small>Booking</small>
                  <strong>{titleCase(detail.bookingStatus)}</strong>
                  <p>ID: {String(detailBooking.bookingReference || detailBooking._id || "").slice(-8)}</p>
                </section>
                <section className="gps-detail-summary-card">
                  <span className="gps-detail-summary-card__icon"><PinIcon /></span>
                  <small>Destination</small>
                  <strong><PinIcon /> {detail.destination}</strong>
                  <p>{formatDate(detail.startDate)} - {formatDate(detail.endDate)}</p>
                </section>
                <section className="gps-detail-summary-card">
                  <span className="gps-detail-summary-card__icon"><StatusIcon type="refresh" /></span>
                  <small>Last Updated</small>
                  <strong>{formatDateTime(detailTracking.lastUpdatedAt || detail.lastUpdate)}</strong>
                  <p>{formatFullDateTime(detailTracking.lastUpdatedAt || detail.lastUpdate)}</p>
                </section>
              </div>

              <div className="gps-detail-layout">
                <main className="gps-detail-main">
                  <section className="gps-map-card">
                    <header className="gps-map-card__head">
                      <span className="gps-map-icon"><StatusIcon type="map" /></span>
                      <div>
                        <h2>Live Location Map</h2>
                        <p>Live location and route monitoring</p>
                      </div>
                      <div className="gps-map-meta">
                        <em>Tracking data may be simulated during testing.</em>
                        <em>Route Source: {routeSourceLabel}</em>
                        <em>Radius: {formatKm(allowedRadius)}</em>
                        <em className={isOutOfZone ? "is-alert" : "is-safe"}>{mapZoneLabel}</em>
                      </div>
                    </header>
                    <TrackingMap
                      booking={detailBooking}
                      tracking={detailTracking}
                      zoneStatus={zoneStatus}
                      allowedRadius={allowedRadius}
                      currentDistance={currentDistance}
                      destination={detail.destination}
                      fallbackNote={fallbackSimulationNote}
                      routeFallbackNote={routeFallbackNote}
                    />
                    <footer className="gps-map-legend">
                      <span><i className="gps-map-legend__pickup" /> Pickup</span>
                      <span><i className="gps-map-legend__destination" /> Destination</span>
                      <span><i className="gps-map-legend__vehicle" /> Current Vehicle</span>
                      <span><i className="gps-map-legend__route" /> {routeLegendLabel}</span>
                      <span><i className={`gps-map-legend__radius ${isOutOfZone ? "is-alert" : ""}`} /> Allowed Route Radius</span>
                    </footer>
                  </section>

                  <section className="gps-route-history-card">
                    <h3>Route History</h3>
                    {routeHistory.length ? (
                      <div className="gps-route-timeline">
                        {routeHistory.map((item, index) => (
                          <div key={`${item.label}-${index}`} className="gps-route-timeline__item">
                            <i />
                            <strong>{item.label}</strong>
                            <small>{formatDateTime(item.time)} - {item.meta}</small>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="gps-route-empty-state">
                        <strong>No route history yet.</strong>
                        <p>Location updates will appear here once tracking starts.</p>
                      </div>
                    )}
                  </section>

                  <section className="gps-simulation-card">
                    <header>
                      <h3>Route Monitoring Controls</h3>
                      {actionMsg ? <span className={`gps-action-message gps-action-message--${actionTone}`}>{actionMsg}</span> : null}
                    </header>
                    <p className="gps-simulation-helper">
                      Tracking data may be simulated during testing. Route monitoring uses the booking pickup and destination locations.
                    </p>
                    {fallbackSimulationNote ? (
                      <p className="gps-simulation-fallback-note">{fallbackSimulationNote}</p>
                    ) : null}
                    {routeFallbackNote ? (
                      <p className="gps-simulation-fallback-note">{routeFallbackNote}</p>
                    ) : null}
                    <div className="gps-simulation-actions">
                      <button type="button" disabled={trackingLoading} onClick={() => runTrackingAction(`/bookings/${bookingId}/tracking/start`, { success: "Live tracking started.", actionKey: "start" })}>{actionKey === "start" ? "Starting..." : "Start Live Monitoring"}</button>
                      <button type="button" disabled={trackingLoading} onClick={() => runTrackingAction(`/bookings/${bookingId}/tracking/stop`, { success: "Tracking stopped.", actionKey: "stop" })}>{actionKey === "stop" ? "Stopping..." : "Stop Tracking"}</button>
                      <button type="button" disabled={trackingLoading} onClick={() => runTrackingAction(`/bookings/${bookingId}/tracking/reset`, { success: "Tracking reset.", actionKey: "reset" })}>{actionKey === "reset" ? "Resetting..." : "Reset Tracking"}</button>
                      <button type="button" disabled={trackingLoading} onClick={() => runTrackingAction(`/bookings/${bookingId}/tracking/tick`, { success: "Tracking update advanced.", actionKey: "tick" })}>{actionKey === "tick" ? "Advancing..." : "Advance Tracking Update"}</button>
                    </div>
                    <div className="gps-simulation-grid">
                      <label>
                        Scenario
                        <select value={scenario} onChange={(event) => handleScenarioChange(event.target.value)}>
                          <option value="normal">Normal Route</option>
                          <option value="slight_detour">Slight Detour</option>
                          <option value="custom_out_of_route">Custom Out-of-Route</option>
                        </select>
                      </label>
                      {isCustomScenario ? (
                        <>
                          <label>
                            Preset Location
                            <select value={preset} onChange={(event) => setPreset(event.target.value)}>
                              {Object.entries(LOCATION_PRESETS).map(([key, value]) => (
                                <option key={key || "none"} value={key}>{value.label}</option>
                              ))}
                            </select>
                          </label>
                          <label>
                            Latitude
                            <input value={customLat} onChange={(event) => { setCustomLat(event.target.value); setScenarioValidation(""); }} disabled={preset !== "custom"} />
                          </label>
                          <label>
                            Longitude
                            <input value={customLng} onChange={(event) => { setCustomLng(event.target.value); setScenarioValidation(""); }} disabled={preset !== "custom"} />
                          </label>
                          <label className="gps-simulation-label">
                            Location Label
                            <input value={customLabel} onChange={(event) => { setCustomLabel(event.target.value); setScenarioValidation(""); }} disabled={preset !== "custom"} />
                          </label>
                        </>
                      ) : null}
                      <button type="button" onClick={applyScenario} disabled={trackingLoading || (isCustomScenario && !hasValidCustomCoords)}>
                        {actionKey === "scenario" ? "Applying..." : scenarioButtonLabel}
                      </button>
                      {isCustomScenario && customScenarioMessage ? (
                        <span className="gps-simulation-validation">{customScenarioMessage}</span>
                      ) : null}
                    </div>
                  </section>
                </main>

                <aside className="gps-detail-side">
                  <section className={`gps-side-card gps-zone-card gps-zone-card--${zoneStatus}`}>
                    <header>
                      <h3><StatusIcon type="zone" /> Route Monitoring</h3>
                      <span>Allowed Route Radius</span>
                    </header>
                    <DetailInfoRow label="Allowed Route Radius" value={formatKm(allowedRadius)} />
                    <DetailInfoRow label="Distance from Expected Route" value={formatKm(currentDistance)} />
                    <DetailInfoRow label="Tracking Status" value={getZoneLabel(zoneStatus)} />
                    {isOutOfZone ? <DetailInfoRow label="Outside Since" value={formatDateTime(detailTracking.lastUpdatedAt)} /> : null}
                    <div className="gps-zone-progress">
                      <span style={{ width: `${distancePercent}%` }} />
                    </div>
                    <div className="gps-zone-scale"><small>0 km</small><small>{formatKm(allowedRadius)} limit</small></div>
                  </section>

                  <section className="gps-side-card gps-vehicle-details-card">
                    <h3>Vehicle Details</h3>
                    <div className="gps-detail-vehicle-image">
                      {detail.image ? <img src={detail.image} alt={detail.vehicleName} /> : <span>{detail.vehicleName.charAt(0)}</span>}
                    </div>
                    <DetailInfoRow label="Vehicle" value={detail.vehicleName} />
                    <DetailInfoRow label="Plate No." value={detail.plateNo} />
                    <DetailInfoRow label="Category" value={detail.category} />
                    <DetailInfoRow label="Fuel" value={detailVehicle.fuel || "-"} />
                    <DetailInfoRow label="Transmission" value={detailVehicle.transmission || "-"} />
                    <DetailInfoRow label="Odometer" value={detailVehicle.odometer ? `${detailVehicle.odometer} km` : "-"} />
                  </section>

                  <section className="gps-side-card">
                    <h3>Customer & Driver</h3>
                    <div className="gps-contact-block"><span>Customer</span><strong>{detail.customerName}</strong><small>{detail.contact}</small></div>
                    <div className="gps-contact-block"><span>Email</span><strong>{detailBooking.email || "-"}</strong></div>
                    <div className="gps-contact-block"><span>Driver</span><strong>{detailDriver.fullName || detailDriver.name || (detailBooking.withDriver ? "Not assigned" : "Self-drive")}</strong><small>{detailDriver.contactNumber || detailDriver.contact || ""}</small></div>
                  </section>

                  <section className="gps-side-card">
                    <h3>Booking Details</h3>
                    <DetailInfoRow label="Booking ID" value={detailBooking.bookingReference || detailBooking._id} />
                    <DetailInfoRow label="Start Date" value={formatDate(detailBooking.startDate)} />
                    <DetailInfoRow label="End Date" value={formatDate(detailBooking.endDate)} />
                    <DetailInfoRow label="Destination" value={detail.destination} />
                    <DetailInfoRow label="Allowed Route Radius" value={formatKm(allowedRadius)} />
                    <DetailInfoRow label="Passengers" value={detailBooking.numberOfPax} />
                    <DetailInfoRow label="Total" value={formatPeso(detailBooking.totalPrice)} />
                  </section>

                  <section className="gps-coordinates-card">
                    <h3>Live Location</h3>
                    <DetailInfoRow label="Latitude" value={hasCoords ? Number(coords.lat).toFixed(6) : "-"} />
                    <DetailInfoRow label="Longitude" value={hasCoords ? Number(coords.lng).toFixed(6) : "-"} />
                    <p>{hasCoords ? getCurrentVehicleLabel(detailTracking.currentLocationLabel) : "Live location unavailable"}</p>
                    <button type="button" disabled={!mapsUrl} onClick={() => window.open(mapsUrl, "_blank", "noopener,noreferrer")}>
                      Open in Google Maps
                    </button>
                  </section>
                </aside>
              </div>
            </>
          ) : null}
        </div>
      </AdminShell>
    );
  }

  return (
    <AdminShell
      pageClassName="admin-shell--live-monitoring"
      eyebrow="FLEET MONITORING"
      title="Live Monitoring"
      subtitle="Monitor active rentals, route status, and location updates from one dashboard."
      actions={headerActions}
    >
      <div className="gps-tracking-page">
        {outOfZoneRow ? (
          <button type="button" className="gps-alert-banner" onClick={() => handleTrack(outOfZoneRow)}>
            <span className="gps-alert-banner__icon"><StatusIcon type="alert" /></span>
            <span className="gps-alert-banner__copy">
              <strong>Out of Route Alert - {counts.outOfZone} vehicle outside the allowed route radius</strong>
              <small>{outOfZoneRow.vehicleName} ({outOfZoneRow.plateNo}) - Click View Alert to review route details.</small>
            </span>
            <span className="gps-alert-banner__action">View Alert</span>
          </button>
        ) : null}

        <div className="gps-summary-grid">
          {[
            { key: "active", label: "Active", value: counts.active, icon: "zone" },
            { key: "delayed", label: "Delayed", value: counts.delayed, icon: "delayed" },
            { key: "offline", label: "No active trip", value: counts.offline, icon: "offline" },
            { key: "returned", label: "Completed", value: counts.returned, icon: "returned" },
            { key: "out_of_zone", label: "Out of Route", value: counts.outOfZone, icon: "alert" },
          ].map((card) => (
            <button
              type="button"
              key={card.key}
              className={`gps-summary-card gps-summary-card--${card.key} ${selectedCard === card.key ? "is-selected" : ""}`}
              onClick={() => handleSummaryClick(card.key)}
            >
              <span><small>{card.label}</small><strong>{loading ? "..." : card.value}</strong></span>
              <em><StatusIcon type={card.icon} /></em>
            </button>
          ))}
        </div>

        <div className="gps-filter-row">
          <label className="gps-search">
            <SearchIcon />
            <input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search vehicle, plate, customer, destination..." />
          </label>
          <select value={gpsFilter} onChange={(event) => setGpsFilter(event.target.value)}>
            <option value="all">All Tracking Status</option>
            <option value="active">Active</option>
            <option value="delayed">Delayed</option>
            <option value="offline">No active trip</option>
            <option value="returned">Completed</option>
          </select>
          <select value={zoneFilter} onChange={(event) => setZoneFilter(event.target.value)}>
            <option value="all">All Route Status</option>
            <option value="normal">On Route</option>
            <option value="out_of_zone">Out of Route</option>
            <option value="unknown">No active trip</option>
          </select>
          <select value={bookingFilter} onChange={(event) => setBookingFilter(event.target.value)}>
            <option value="all">All Booking Status</option>
            <option value="approved">Approved</option>
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
          </select>
          <button type="button" className="btn gps-clear-btn" onClick={clearListFilters}>
            Clear
          </button>
        </div>
        <div className="gps-date-filter-row">
          <span className="gps-date-filter-row__label">Filter by rental date:</span>
          {["all", "today", "tomorrow", "this_week", "custom"].map((key) => (
            <button
              key={key}
              type="button"
              className={`gps-date-pill${dateFilter === key ? " is-active" : ""}`}
              onClick={() => { setDateFilter(key); if (key !== "custom") setCustomDate(""); }}
            >
              {key === "all" ? "All Dates" : key === "today" ? "Today" : key === "tomorrow" ? "Tomorrow" : key === "this_week" ? "This Week" : "Custom Date"}
            </button>
          ))}
          {dateFilter === "custom" ? (
            <input
              type="date"
              className="gps-date-custom-input"
              value={customDate}
              onChange={(event) => setCustomDate(event.target.value)}
              aria-label="Select custom date"
            />
          ) : null}
        </div>

        <section className="gps-table-card">
          <header className="gps-table-card__head"><h2>Live Monitoring</h2><span>{filteredRows.length} vehicles</span></header>
          <div className="gps-table-wrap">
            <table className="gps-table">
              <colgroup>
                <col className="gps-col-vehicle" />
                <col className="gps-col-people" />
                <col className="gps-col-destination" />
                <col className="gps-col-radius" />
                <col className="gps-col-dates" />
                <col className="gps-col-booking" />
                <col className="gps-col-gps" />
                <col className="gps-col-zone" />
                <col className="gps-col-update" />
                <col className="gps-col-action" />
              </colgroup>
              <thead>
                <tr>
                  <th>Vehicle</th><th>Customer / Driver</th><th>Destination</th><th>Radius</th><th>Rental Dates</th><th>Booking</th><th>Tracking Status</th><th>Route Status</th><th>Last Update</th><th>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="10" className="gps-empty-cell">Loading live monitoring data...</td></tr>
                ) : error ? (
                  <tr><td colSpan="10" className="gps-empty-cell gps-empty-cell--error">{error}</td></tr>
                ) : filteredRows.length ? (
                  filteredRows.map((row) => (
                    <tr key={row.id} className={row.zoneStatus === "out_of_zone" ? "is-out-zone" : ""} onClick={() => handleTrack(row)}>
                      <td className="gps-table-cell gps-table-cell--vehicle">
                        <div className="gps-vehicle-cell">
                          <div className="gps-vehicle-thumb">
                            {row.image ? <img src={row.image} alt={row.vehicleName} /> : <span>{row.vehicleName.charAt(0)}</span>}
                          </div>
                          <div className="gps-vehicle-cell__copy">
                            <strong title={row.vehicleName}>{row.vehicleName}</strong>
                            <small title={`${row.plateNo} - ${row.category}`}>{row.plateNo} - {row.category}</small>
                          </div>
                        </div>
                      </td>
                      <td className="gps-table-cell gps-table-cell--people">
                        <div className="gps-two-line gps-people-cell">
                          <strong title={row.customerName}>{row.customerName}</strong>
                          <small title={row.contact}>{row.contact}</small>
                        </div>
                      </td>
                      <td><span className="gps-destination"><PinIcon />{row.destination}</span></td>
                      <td><span className="gps-radius-pill">{row.radiusKm} km</span></td>
                      <td><span className="gps-date-range">{formatDate(row.startDate)}<small>-&gt; {formatDate(row.endDate)}</small></span></td>
                      <td><span className={`gps-badge gps-badge--booking-${row.bookingStatus}`}>{titleCase(row.bookingStatus)}</span></td>
                      <td><span className={`gps-status gps-status--${row.gpsStatus}`}><i /><span>{getTrackingStatusLabel(row.gpsStatus)}</span></span></td>
                      <td><span className={`gps-badge gps-badge--zone-${row.zoneStatus}`}>{row.zoneStatus === "out_of_zone" ? <StatusIcon type="alert" /> : <StatusIcon type={row.zoneStatus === "normal" ? "active" : "returned"} />}{getZoneLabel(row.zoneStatus)}</span></td>
                      <td><span className="gps-last-update">{formatDateTime(row.lastUpdate)}</span></td>
                      <td><button type="button" className={`gps-action-btn ${row.zoneStatus === "out_of_zone" ? "gps-action-btn--alert" : ""}`} onClick={(event) => { event.stopPropagation(); handleTrack(row); }}><PinIcon />{row.zoneStatus === "out_of_zone" ? "View Alert" : "View Live Monitoring"}</button></td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan="10" className="gps-empty-cell">{resolvedDateFilter ? "No tracked rentals found for this date." : "No active trip matches the current filters."}</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <footer className="gps-table-card__footer">
            <div className="gps-legend">
              <strong>Legend:</strong>
              <span><i className="gps-dot gps-dot--active" /> Active</span>
              <span><i className="gps-dot gps-dot--delayed" /> Delayed</span>
              <span><i className="gps-dot gps-dot--offline" /> No active trip</span>
              <span><i className="gps-dot gps-dot--returned" /> Completed</span>
              <span><i className="gps-dot gps-dot--out-of-zone" /> Out of Route</span>
              <span><i className="gps-dot gps-dot--normal-zone" /> On Route</span>
            </div>
          </footer>
        </section>
      </div>
    </AdminShell>
  );
}

export default function GPSTracking() {
  const { bookingId } = useParams();
  const [managementView, setManagementView] = useState("fleet");
  const [replaySelection, setReplaySelection] = useState(null);
  if (bookingId && !GPS_SIMULATION_CONTROLS_ENABLED) {
    return <AdminShell pageClassName="admin-shell--live-monitoring" eyebrow="FLEET MONITORING" title="Simulation controls unavailable"
      subtitle="Simulation and diagnostic controls are not enabled in this environment." />;
  }
  if (bookingId) return <LegacyBookingTracking />;
  if (managementView === "fleet") {
    return <GpsFleetDashboard activeView={managementView} onViewChange={setManagementView} />;
  }
  if (managementView === "replay") {
    return <GpsRouteReplay activeView={managementView} onViewChange={setManagementView}
      initialSelection={replaySelection} onInitialSelectionConsumed={() => setReplaySelection(null)} />;
  }
  if (managementView === "trips") {
    return <GpsTripsActivity activeView={managementView} onViewChange={setManagementView}
      onReplayTrip={(selection) => { setReplaySelection(selection); setManagementView("replay"); }} />;
  }
  return <GpsDeviceAssignmentPanel activeView={managementView} onViewChange={setManagementView} />;
}
